import { Injectable } from '@angular/core';
import { LocalStorageService } from '../local-storage/local-storage.service';

export interface LastLoginConfig {
    idp: string;
    identifier?: string;
    timestamp: number;
}

@Injectable({ providedIn: 'root' })
export class LastLoginConfigService {
    private readonly STORAGE_KEY = 'lastSuccessfulLogin';
    private readonly MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

    constructor(private localStorageService: LocalStorageService) {}

    /**
     * Save the last successful login configuration
     * @param idp - The identity provider used (e.g., 'sap.ids', 'custom.idp')
     * @param identifier - Optional OIDC identifier (only for custom.idp)
     */
    save(idp: string, identifier?: string): void {
        const config: LastLoginConfig = {
            idp,
            identifier: idp === 'custom.idp' ? identifier : undefined,
            timestamp: Date.now()
        };
        this.localStorageService.set(this.STORAGE_KEY, JSON.stringify(config));
    }

    /**
     * Load the last successful login configuration
     * @returns The stored configuration if valid, null otherwise
     */
    load(): LastLoginConfig | null {
        const stored = this.localStorageService.get(this.STORAGE_KEY);
        if (!stored) {
            return null;
        }

        try {
            const config = JSON.parse(stored) as LastLoginConfig;
            if (this.isValid(config)) {
                return config;
            }
            // Remove expired/invalid config
            this.clear();
            return null;
        } catch (e) {
            console.warn('Invalid lastSuccessfulLogin storage:', e);
            this.clear();
            return null;
        }
    }

    /**
     * Clear the stored login configuration
     */
    clear(): void {
        this.localStorageService.remove(this.STORAGE_KEY);
    }

    /**
     * Validate a login configuration
     * @param config - The configuration to validate
     * @returns true if valid, false otherwise
     */
    private isValid(config: any): config is LastLoginConfig {
        if (!config || typeof config !== 'object') {
            return false;
        }

        // Check required fields
        if (!config.idp || typeof config.idp !== 'string') {
            return false;
        }

        if (!config.timestamp || typeof config.timestamp !== 'number') {
            return false;
        }

        // Check valid IDP values
        const validIdps = ['sap.ids', 'sap.opscurity', 'sap.opscurity-dev', 'custom.idp'];
        if (!validIdps.includes(config.idp)) {
            return false;
        }

        // Check expiration
        if (Date.now() - config.timestamp > this.MAX_AGE_MS) {
            return false;
        }

        return true;
    }
}
